import React, { lazy, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";

import Layout from "@/components/Layout";
import SuspensedView from "../components/SuspensedView";
import useAuth from "@/auth/useAuth";

const HomePage = lazy(() => import("../pages/HomePage"));
const NotFoundPage = lazy(() => import("../pages/404Page"));
const ProfilePage = lazy(() => import("../pages/user/ProfilePage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const RulesPage = lazy(() => import("@/pages/RulesPage"));
const CategoryNewsPage = lazy(() => import("@/pages/news/CategoryNewsPage"));
const SubmitNewsPage = lazy(() => import("@/pages/news/SubmitNewsPage"));
const NewsPage = lazy(() => import("@/pages/news/NewsPage"));
const SingleNewsPage = lazy(() => import("@/pages/news/SingleNewsPage"));
const NetworkPage = lazy(() => import("@/pages/network/NetworkPage"));
const NetworksPage = lazy(() => import("@/pages/network/NetworksPage"));
const SingleNetworkPage = lazy(() =>
  import("@/pages/network/SingleNetworkPage")
);
const UserProfilePage = lazy(() => import("@/pages/user/UserProfilePage"));
const Followers = lazy(() => import("@/pages/user/Followers"));
const Following = lazy(() => import("@/pages/user/Following"));
const LikedContent = lazy(() => import("@/pages/user/LikedContent"));
const CreatePage = lazy(() => import("@/pages/CreatePage"));
const ExplorePage = lazy(() => import("@/pages/ExplorePage"));
const SinglePostPage = lazy(() => import("@/pages/SinglePostPage"));
const WikiVillageDetailPage = lazy(() =>
  import("@/pages/wiki/WikiVillageDetailPage")
);
const WikiJobDetailPage = lazy(() => import("@/pages/wiki/WikiJobDetailPage"));
const WikiToolDetailPage = lazy(() =>
  import("@/pages/wiki/WikiToolDetailPage")
);
const WikiBoomDetailPage = lazy(() =>
  import("@/pages/wiki/WikiBoomDetailPage")
);
const WikiPage = lazy(() => import("@/pages/wiki/WikiPage"));
const ShopPage = lazy(() => import("@/pages/shop/ShopPage"));
const ShopItemPage = lazy(() => import("@/pages/shop/ShopItemPage"));
const AllPage = lazy(() => import("@/pages/AllPage"));

// Functional Components
const FindModelIdComponent = lazy(() =>
  import("@/components/FindModelIdComponent")
);

const PrivateRouter = () => {
  const { setCurrentUser } = useAuth();

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    preload();
  }, []);

  const preload = async () => {
    const user = localStorage.getItem("rosta-tv");

    if (!!user) {
      setCurrentUser(JSON.parse(user));
      setIsReady(true);
    }
  };

  const getSuspensedRoute = (path, Page) => (
    <Route
      path={path}
      element={
        <SuspensedView>
          <Page />
        </SuspensedView>
      }
    />
  );

  if (!isReady) return null;

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        {getSuspensedRoute("/", HomePage)}
        {/* {getSuspensedRoute("/all", AllPage)} */}
        {getSuspensedRoute("/profile", UserProfilePage)}
        {getSuspensedRoute("/edit-profile", ProfilePage)}
        {getSuspensedRoute("/services", ServicesPage)}
        {getSuspensedRoute("/about", AboutPage)}
        {getSuspensedRoute("/rules", RulesPage)}
        {getSuspensedRoute("/submit-news", SubmitNewsPage)}
        {getSuspensedRoute("/news", NewsPage)}
        {getSuspensedRoute("/news/category/:categoryTitle", CategoryNewsPage)}
        {getSuspensedRoute("/news/:slug", SingleNewsPage)}
        {getSuspensedRoute("/users/:userId/followers", Followers)}
        {getSuspensedRoute("/users/:userId/following", Following)}
        {getSuspensedRoute("/users/:userId/liked", LikedContent)}
        {getSuspensedRoute("/user/:userId", UserProfilePage)}
        {getSuspensedRoute("/create", CreatePage)}
        {getSuspensedRoute("/create/:id", CreatePage)}
        {getSuspensedRoute("/explore", ExplorePage)}
        {getSuspensedRoute("/post/:id", SinglePostPage)}
        {getSuspensedRoute("/wiki", WikiPage)}
        {getSuspensedRoute("/wiki/job/:jobId", WikiJobDetailPage)}
        {getSuspensedRoute("/wiki/tool/:toolId", WikiToolDetailPage)}
        {getSuspensedRoute("/wiki/boom/:boomId", WikiBoomDetailPage)}
        {getSuspensedRoute("/networks", NetworksPage)}
        {getSuspensedRoute("/networks/:id", NetworkPage)}
        {getSuspensedRoute("/network/:slug", SingleNetworkPage)}
        {getSuspensedRoute("/shop", ShopPage)}
        {getSuspensedRoute("/shop/:itemId", ShopItemPage)}
        {getSuspensedRoute("/share/:model/:shortId", FindModelIdComponent)}
        {getSuspensedRoute("/:villageSlug", WikiVillageDetailPage)}
        {getSuspensedRoute("*", NotFoundPage)}
      </Route>

      {getSuspensedRoute("*", NotFoundPage)}
    </Routes>
  );
};

export default PrivateRouter;
